import { motion, useReducedMotion } from "framer-motion";

/*
 * BlueprintIntro
 * A short (< 1.5s) blueprint-to-photo reveal. The Harris Homes roof outline
 * is drawn in gold linework, then crossfades into the hero image.
 *
 * Uses framer-motion for path drawing, with reduced-motion fallback.
 */
export default function BlueprintIntro({ imageUrl, alt }) {
    const reduce = useReducedMotion();

    return (
        <div className="absolute inset-0 overflow-hidden">
            {/* Background image (fades in) */}
            <motion.div
                className="absolute inset-0"
                initial={{ opacity: reduce ? 1 : 0, scale: reduce ? 1 : 1.06 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: reduce ? 0 : 1.0, delay: reduce ? 0 : 0.7, ease: [0.16, 1, 0.3, 1] }}
            >
                <img
                    src={imageUrl}
                    alt={alt}
                    width={1600}
                    height={1200}
                    fetchPriority="high"
                    className="w-full h-full object-cover"
                    loading="eager"
                />
                {/* Darkening + vignette layers */}
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "linear-gradient(180deg, rgba(10,9,8,0.55) 0%, rgba(10,9,8,0.35) 40%, rgba(10,9,8,0.85) 100%)",
                    }}
                />
                <div
                    className="absolute inset-0"
                    style={{
                        background:
                            "radial-gradient(ellipse at center, transparent 35%, rgba(10,9,8,0.55) 100%)",
                    }}
                />
            </motion.div>

            {/* Blueprint SVG (draws then fades) */}
            {!reduce && (
                <motion.svg
                    viewBox="0 0 800 400"
                    className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-[88vw] max-w-[860px] h-auto pointer-events-none"
                    fill="none"
                    initial={{ opacity: 0 }}
                    animate={{ opacity: [0, 1, 1, 0] }}
                    transition={{
                        duration: 1.4,
                        times: [0, 0.15, 0.75, 1],
                        ease: "easeInOut",
                    }}
                    aria-hidden="true"
                >
                    {/* Faint blueprint grid */}
                    <motion.g
                        stroke="#C5A059"
                        strokeOpacity="0.10"
                        strokeWidth="0.6"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.6 }}
                    >
                        {[...Array(16)].map((_, i) => (
                            <line key={`v${i}`} x1={i * 50} y1="0" x2={i * 50} y2="400" />
                        ))}
                        {[...Array(9)].map((_, i) => (
                            <line key={`h${i}`} x1="0" y1={i * 50} x2="800" y2={i * 50} />
                        ))}
                    </motion.g>

                    {/* Outer roof outline */}
                    <motion.path
                        d="M 100 290 L 400 90 L 700 290"
                        stroke="#C5A059"
                        strokeWidth="2"
                        strokeLinecap="square"
                        strokeLinejoin="miter"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
                    />
                    {/* Inner roof line */}
                    <motion.path
                        d="M 180 290 L 400 145 L 620 290"
                        stroke="#C5A059"
                        strokeWidth="1.5"
                        strokeOpacity="0.85"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.85, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
                    />
                    {/* Window (small square) */}
                    <motion.rect
                        x="378"
                        y="200"
                        width="44"
                        height="50"
                        stroke="#C5A059"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0, opacity: 0 }}
                        animate={{ pathLength: 1, opacity: 1 }}
                        transition={{ duration: 0.4, delay: 0.55 }}
                    />
                    <motion.line
                        x1="400"
                        y1="200"
                        x2="400"
                        y2="250"
                        stroke="#C5A059"
                        strokeWidth="1"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: 0.75 }}
                    />
                    <motion.line
                        x1="378"
                        y1="225"
                        x2="422"
                        y2="225"
                        stroke="#C5A059"
                        strokeWidth="1"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.3, delay: 0.8 }}
                    />
                    {/* Baseline */}
                    <motion.line
                        x1="100"
                        y1="290"
                        x2="700"
                        y2="290"
                        stroke="#C5A059"
                        strokeWidth="1.5"
                        initial={{ pathLength: 0 }}
                        animate={{ pathLength: 1 }}
                        transition={{ duration: 0.6, delay: 0.25 }}
                    />
                </motion.svg>
            )}
        </div>
    );
}
